/**
 * LiveChat Integration Module
 *
 * This module provides a clean, on-demand integration with LiveChat.
 * The chat widget is only loaded and opened when the user explicitly
 * clicks a chat button, improving page performance and user experience.
 *
 * Usage:
 * - Import this module in your main.js
 * - Call initLiveChat() to set up click handlers
 * - Add data-livechat-trigger attribute to any button/link that should open chat
 *
 * @see https://www.livechat.com/
 */

// LiveChat configuration
// License is read from meta tag (set in Pug template from LIVECHAT_LICENSE env var)
const metaTag = document.querySelector('meta[name="livechat-license"]');
const LIVECHAT_LICENSE = metaTag?.content ? parseInt(metaTag.content, 10) : null;
const LIVECHAT_INTEGRATION_NAME = 'manual_onboarding';
const LIVECHAT_PRODUCT_NAME = 'livechat';

// Track if LiveChat has been loaded
let isLiveChatLoaded = false;
let isLiveChatLoading = false;

/**
 * Loads the LiveChat tracking script dynamically
 * @returns {Promise} Resolves when LiveChat widget is fully ready
 */
function loadLiveChatScript() {
  return new Promise((resolve, reject) => {
    if (isLiveChatLoaded) {
      resolve();
      return;
    }

    if (isLiveChatLoading) {
      // If already loading, wait for it to complete
      const checkLoaded = setInterval(() => {
        if (isLiveChatLoaded) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 100);
      return;
    }

    isLiveChatLoading = true;

    // Initialize LiveChat configuration
    window.__lc = window.__lc || {};
    window.__lc.license = LIVECHAT_LICENSE;
    window.__lc.integration_name = LIVECHAT_INTEGRATION_NAME;
    window.__lc.product_name = LIVECHAT_PRODUCT_NAME;
    // Prevent auto-initialization - we'll control when to show the widget
    window.__lc.asyncInit = true;

    // Set up the LiveChat widget API
    const { slice } = [];
    function i(n) {
      return window.LiveChatWidget._h
        ? window.LiveChatWidget._h.apply(null, n)
        : window.LiveChatWidget._q.push(n);
    }

    window.LiveChatWidget = window.LiveChatWidget || {
      _q: [],
      _h: null,
      _v: '2.0',
      on: function () {
        i(['on', slice.call(arguments)]);
      },
      once: function () {
        i(['once', slice.call(arguments)]);
      },
      off: function () {
        i(['off', slice.call(arguments)]);
      },
      get: function () {
        if (!window.LiveChatWidget._h) {
          throw new Error("[LiveChatWidget] You can't use getters before load.");
        }
        return i(['get', slice.call(arguments)]);
      },
      call: function () {
        i(['call', slice.call(arguments)]);
      },
      init: function () {
        const script = document.createElement('script');
        script.async = true;
        script.type = 'text/javascript';
        script.src = 'https://cdn.livechatinc.com/tracking.js';
        script.onerror = () => {
          isLiveChatLoading = false;
          reject(new Error('Failed to load LiveChat script'));
        };
        document.head.appendChild(script);
      },
    };

    // Listen for widget ready event - this is when we can safely use the widget
    window.LiveChatWidget.on('ready', () => {
      isLiveChatLoaded = true;
      isLiveChatLoading = false;
      // Hide the widget initially - we'll show it when user clicks
      window.LiveChatWidget.call('hide');
      resolve();
    });

    // Initialize the script
    window.LiveChatWidget.init();
  });
}

/**
 * Opens the LiveChat widget
 * Loads the script if not already loaded
 */
export async function openLiveChat() {
  try {
    await loadLiveChatScript();

    // Maximize the chat window
    if (window.LiveChatWidget) {
      window.LiveChatWidget.call('maximize');
    }
  } catch (error) {
    console.error('Failed to open LiveChat:', error);
    // Fallback: open LiveChat in a new window
    window.open(`https://www.livechat.com/chat-with/${LIVECHAT_LICENSE}/`, '_blank');
  }
}

/**
 * Preloads the LiveChat script without opening the widget
 * Called on hover/focus for faster click response
 */
export function preloadLiveChat() {
  if (!LIVECHAT_LICENSE || isLiveChatLoaded || isLiveChatLoading) {
    return;
  }
  loadLiveChatScript().catch(() => {
    // Silently ignore preload errors - will retry on click
  });
}

/**
 * Initializes LiveChat button click handlers
 * Looks for elements with data-livechat-trigger attribute
 * or the .js-livechat-trigger class
 */
export function initLiveChat() {
  // Skip initialization if no license is configured
  if (!LIVECHAT_LICENSE) {
    return;
  }

  // Find all chat trigger elements
  const chatTriggers = document.querySelectorAll('[data-livechat-trigger], .js-livechat-trigger');

  chatTriggers.forEach((trigger) => {
    // Preload on hover/focus for faster click response
    trigger.addEventListener('mouseenter', preloadLiveChat, { once: true });
    trigger.addEventListener('focus', preloadLiveChat, { once: true });

    // Open on click
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      openLiveChat();
    });
  });

  // Also preload when user has been idle for a few seconds (optional boost)
  if ('requestIdleCallback' in window) {
    requestIdleCallback(
      () => {
        preloadLiveChat();
      },
      { timeout: 5000 }
    );
  }
}

// Export for use in other modules
export default {
  init: initLiveChat,
  open: openLiveChat,
  preload: preloadLiveChat,
};
